import React from 'react';
import BaseFactory from './BaseFactory';
import {
  getFaqsAnchorTag,
  getBlogAnchorTag,
  hasLocationHours,
  getLocationDistanceInMiles,
  getClinicAnchorTag,
  hasBrandName,
  getBrandName,
  getCityAnchorTag,
  getNearbyBrandLocation,
  NoTopMarginParagraph,
  NoBottomMarginParagraph,
  hasCustomDescription,
} from './util';
import { isEmptyArray } from '../../../core/util/array';
import { isEmptyObject } from '../../../core/util/object';
import CustomDescriptionSection from './CustomDescriptionSection';
import { SOLV_SECTION_TITLE } from './constants';

class NonPartnerUrgentCareV2 extends BaseFactory {
  constructor(location, nearbyLocations, properties) {
    super(location, properties);

    if (!isEmptyObject(nearbyLocations)) {
      this.nearbyLocations = nearbyLocations;
      this.nearbyClinics = nearbyLocations.nearby;
      // nearby brand location
      const nearbyBrandClinic = getNearbyBrandLocation(nearbyLocations);
      if (!isEmptyObject(nearbyBrandClinic)) {
        this.brandName = getBrandName(nearbyBrandClinic);
        this.nearbyBrandClinicAnchorTag = getClinicAnchorTag(nearbyBrandClinic, 'sister facility');
        this.nearbyBrandClinicCityAnchorTag = getCityAnchorTag(nearbyBrandClinic);
        this.nearbyBrandClinicDistance = getLocationDistanceInMiles(nearbyBrandClinic);
      }
    }
  }

  getDescription() {
    return (
      `${this.clinicName} is a ${this.clinicalCategory} located ${this.address}, ${this.city}, ` +
      `${this.state}, ${this.zip} providing immediate, non-life-threatening healthcare` +
      `services to the ${this.city} area. For more information, call ${this.clinicName}` +
      `${this.phoneString}.`
    );
  }

  getKeywords() {
    return (
      `${this.clinicName}, ${this.clinicName}, ${this.city}, ${this.clinicName} ${this.zip}, ` +
      `urgent care ${this.city}, urgent care ${this.zip}, ` +
      `walk-in clinic ${this.city}, walk-in clinic ${this.zip}, ${this.city} ${this.state} ` +
      `clinic, ${this.zip} clinic`
    );
  }

  getBody() {
    return (
      <span>
        <CustomDescriptionSection location={this.location} />
        <NoTopMarginParagraph>
          {hasCustomDescription(this.location) && (
            <span>
              {SOLV_SECTION_TITLE}
              <br />
            </span>
          )}
          {this.clinicName} is an urgent care center in {this.cityAnchorTag}
          {hasLocationHours(this.location) && (
            <span>
              {' '}
              and is open {this.nextOpenDayName} from {this.nextOpenDayHoursStr}
            </span>
          )}
          . They are located at {this.fullAddress}
          {hasLocationHours(this.location) && (
            <span> and open {this.numDaysOpenString} per week</span>
          )}
          .
        </NoTopMarginParagraph>
        <p>
          {isEmptyArray(this.glowingWords) && this.rating && (
            <span>
              Patients have given an average rating of {this.rating} stars out of 5 for their
              experience at <b>{this.clinicName}</b>.{' '}
            </span>
          )}
          {!isEmptyArray(this.glowingWords) && !this.rating && (
            <span>
              Patients have used words like {this.glowingWordsString} to describe their experience
              at <b>{this.clinicName}</b>.{' '}
            </span>
          )}
          {!isEmptyArray(this.glowingWords) && this.rating && (
            <span>
              Patients have used words like {this.glowingWordsString} to describe their experience
              at <b>{this.clinicName}</b> which has garnered an average rating of {this.rating}{' '}
              stars out of 5.{' '}
            </span>
          )}
        </p>
        <p>
          Nationally, we are seeing urgent care patients wait an average of 15-30 minutes before
          being seen by a healthcare provider, which would likely be the case at {this.clinicName}.
          {hasBrandName(this.nearbyLocations) && (
            <span>
              {' '}
              As part of the {this.brandName} group, they have a {this.nearbyBrandClinicAnchorTag}{' '}
              in {this.nearbyBrandClinicCityAnchorTag} {this.nearbyBrandClinicDistance} miles away
              that you may want to consider if the wait gets too long.
            </span>
          )}
        </p>
        <NoBottomMarginParagraph>
          If you are looking to learn more about urgent care including statistics, trends,{' '}
          {getFaqsAnchorTag()} and other useful information, check out our {getBlogAnchorTag()} to
          empower your knowledge as a healthcare consumer. {this.cityGuideContent}
        </NoBottomMarginParagraph>
      </span>
    );
  }
}

export default NonPartnerUrgentCareV2;
